import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../AuthContext'
import { API_BASE } from '../config'
import { C } from '../styles/tokens'
import { photoUrl } from '../utils/photoUrl'

// Pharmacist verification queue — lists orders whose prescription items are
// awaiting verification, shows the linked prescription, and lets a pharmacist
// approve (→ dispatch) or reject the order. Gated to staff roles.
export default function PharmacyQueue() {
  const { user, fetchWithAuth } = useAuth()
  const navigate = useNavigate()
  const [orders, setOrders] = React.useState([])
  const [loading, setLoading] = React.useState(true)
  const [busy, setBusy] = React.useState(null)
  const [detail, setDetail] = React.useState({})   // orderId → { open, loading, uploads, appts }

  // Lazy-load the patient's actual prescriptions so the pharmacist can verify
  const viewRx = async (o) => {
    if (detail[o.id]) { setDetail(d => ({ ...d, [o.id]: { ...d[o.id], open: !d[o.id].open } })); return }
    setDetail(d => ({ ...d, [o.id]: { open: true, loading: true } }))
    try {
      const [pr, fu] = await Promise.all([
        fetchWithAuth(`${API_BASE}/api/prescriptions?email=${encodeURIComponent(o.email)}`).then(r => r.ok ? r.json() : { prescriptions: [] }),
        fetchWithAuth(`${API_BASE}/api/followups?email=${encodeURIComponent(o.email)}`).then(r => r.ok ? r.json() : { followups: [] }),
      ])
      setDetail(d => ({ ...d, [o.id]: { open: true, loading: false, uploads: pr.prescriptions || [], appts: (fu.followups || []).filter(a => a.medication || a.prescription) } }))
    } catch {
      setDetail(d => ({ ...d, [o.id]: { open: true, loading: false, uploads: [], appts: [] } }))
    }
  }

  const load = React.useCallback(async () => {
    setLoading(true)
    try {
      const r = await fetchWithAuth(`${API_BASE}/api/pharmacy/orders`)
      if (r.ok) { const j = await r.json(); setOrders(j.orders || []) }
    } catch {}
    setLoading(false)
  }, [fetchWithAuth])

  React.useEffect(() => {
    if (!user) return
    if (!['admin', 'doctor', 'pharmacist'].includes(user.role)) { navigate('/'); return }
    load()
  }, [user, load, navigate])

  const verify = async (id, action) => {
    if (action === 'reject' && !window.confirm('Reject this prescription and cancel the order?')) return
    setBusy(id)
    try {
      const r = await fetchWithAuth(`${API_BASE}/api/pharmacy/orders/${id}/verify`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action }),
      })
      if (r.ok) setOrders(o => o.filter(x => x.id !== id))
    } catch {}
    setBusy(null)
  }

  const parse = (str, d = []) => { try { return JSON.parse(str || '') ?? d } catch { return d } }

  return (
    <div style={{ minHeight: '100vh', background: '#F7FAF8', fontFamily: "'DM Sans',sans-serif", padding: '2rem 1rem 4rem' }}>
      <div style={{ maxWidth: 760, margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
          <h1 style={{ fontFamily: "'DM Serif Display',serif", fontSize: '1.6rem', color: C.g900, margin: 0 }}>Pharmacy · Prescription Verification</h1>
          <button onClick={load} style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 8, padding: '.4rem .8rem', cursor: 'pointer', fontSize: '.8rem', color: C.g700, fontWeight: 700 }}>↻ Refresh</button>
        </div>
        <div style={{ fontSize: '.85rem', color: C.muted, marginBottom: '1.5rem' }}>{loading ? 'Loading…' : `${orders.length} order(s) awaiting verification`}</div>

        {!loading && orders.length === 0 && (
          <div style={{ textAlign: 'center', color: C.muted, padding: '3rem 1rem' }}>
            <div style={{ fontSize: '2rem', opacity: .3, marginBottom: 8 }}>✓</div>
            Nothing to verify right now.
          </div>
        )}

        {orders.map(o => {
          const items = parse(o.items), scripts = parse(o.rxPrescriptions), addr = parse(o.address, {})
          return (
            <div key={o.id} style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 14, padding: '1.2rem', marginBottom: '1rem', boxShadow: '0 2px 10px rgba(13,34,24,.05)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                <div style={{ fontWeight: 800, color: C.g900 }}>{o.email}</div>
                <div style={{ fontSize: '.8rem', color: C.muted }}>₹{o.amount} · {(o.createdAt || '').slice(0, 10)}</div>
              </div>

              {/* Prescription items to verify */}
              <div style={{ marginTop: 10, background: '#FFF8E1', border: '1px solid #FFE082', borderRadius: 10, padding: '.8rem .9rem' }}>
                <div style={{ fontSize: '.68rem', fontWeight: 800, color: '#7a5b12', letterSpacing: '.05em', marginBottom: 6 }}>℞ PRESCRIPTION ITEMS</div>
                {scripts.length === 0 && <div style={{ fontSize: '.8rem', color: '#7a5b12' }}>{parse(o.rxItems).join(', ')}</div>}
                {scripts.map((s, i) => (
                  <div key={i} style={{ fontSize: '.82rem', color: '#5a4a12', marginBottom: 3, lineHeight: 1.4 }}>
                    {s.matched ? '✓ ' : '⚠ '}<b>{s.item}</b> — {s.matched
                      ? `prescribed by ${s.doctor}${s.date ? ' · ' + String(s.date).slice(0, 10) : ''} (${s.source})`
                      : (s.source === 'uploaded_unmatched' ? 'prescription uploaded — verify against the upload below'
                        : s.source === 'patient_declared' ? 'patient declared — verify manually'
                        : 'no prescription on file')}
                  </div>
                ))}
              </div>

              <div style={{ fontSize: '.78rem', color: C.muted, marginTop: 8 }}><b>All items:</b> {items.map(i => `${i.name} ×${i.quantity || 1}`).join(', ')}</div>
              <div style={{ fontSize: '.78rem', color: C.muted, marginTop: 4 }}><b>Deliver to:</b> {[addr.name, addr.flat, addr.area, addr.city, addr.state, addr.pincode].filter(Boolean).join(', ')}{addr.phone ? ` · ${addr.phone}` : ''}</div>

              {/* Actual prescription content — the pharmacist verifies against this */}
              <button onClick={() => viewRx(o)} style={{ marginTop: 12, background: C.g50, border: `1px solid ${C.border}`, borderRadius: 8, padding: '.5rem .9rem', cursor: 'pointer', fontSize: '.78rem', fontWeight: 700, color: C.g700, fontFamily: "'DM Sans',sans-serif" }}>
                {detail[o.id]?.open ? '▲ Hide prescription' : '🔍 View prescription'}
              </button>
              {detail[o.id]?.open && (
                <div style={{ marginTop: 10, border: `1px solid ${C.border}`, borderRadius: 10, padding: '.9rem', background: '#FAFCFB' }}>
                  {detail[o.id].loading && <div style={{ fontSize: '.8rem', color: C.muted }}>Loading prescription…</div>}
                  {!detail[o.id].loading && (detail[o.id].appts || []).length === 0 && (detail[o.id].uploads || []).length === 0 && (
                    <div style={{ fontSize: '.8rem', color: '#C0392B' }}>⚠ No prescription found for this patient — contact them before dispensing.</div>
                  )}
                  {(detail[o.id].appts || []).map((a, i) => (
                    <div key={'a' + i} style={{ marginBottom: 10 }}>
                      <div style={{ fontSize: '.68rem', fontWeight: 800, color: C.g700, letterSpacing: '.04em' }}>REVIVE DOCTOR PRESCRIPTION</div>
                      <div style={{ fontSize: '.74rem', color: C.muted, margin: '2px 0 4px' }}>{a.doctor || 'Dermatologist'}{a.date ? ' · ' + String(a.date).slice(0, 10) : ''}{a.diagnosis ? ' · ' + a.diagnosis : ''}</div>
                      <div style={{ fontSize: '.82rem', color: C.text, whiteSpace: 'pre-wrap', lineHeight: 1.5, background: '#fff', border: `1px solid ${C.border}`, borderRadius: 8, padding: '.6rem .7rem' }}>{a.medication || a.prescription}</div>
                    </div>
                  ))}
                  {(detail[o.id].uploads || []).map((u, i) => (
                    <div key={'u' + i} style={{ marginBottom: 10 }}>
                      <div style={{ fontSize: '.68rem', fontWeight: 800, color: C.g700, letterSpacing: '.04em' }}>UPLOADED PRESCRIPTION</div>
                      <div style={{ fontSize: '.74rem', color: C.muted, margin: '2px 0 4px' }}>{u.doctorName || 'Uploaded'}{u.createdAt ? ' · ' + String(u.createdAt).slice(0, 10) : ''}</div>
                      {u.medicationText && <div style={{ fontSize: '.82rem', color: C.text, whiteSpace: 'pre-wrap', lineHeight: 1.5, marginBottom: 6 }}>{u.medicationText}</div>}
                      {u.imageFilename && <a href={photoUrl(u.imageFilename)} target="_blank" rel="noopener noreferrer"><img src={photoUrl(u.imageFilename)} alt="prescription" style={{ maxWidth: '100%', borderRadius: 8, border: `1px solid ${C.border}` }} /></a>}
                    </div>
                  ))}
                </div>
              )}

              <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
                <button disabled={busy === o.id} onClick={() => verify(o.id, 'approve')} style={{ flex: 1, background: busy === o.id ? '#9bbda6' : C.g700, color: '#fff', border: 'none', borderRadius: 10, padding: '.75rem', fontWeight: 800, fontSize: '.85rem', cursor: 'pointer', fontFamily: "'DM Sans',sans-serif" }}>✓ Approve &amp; dispatch</button>
                <button disabled={busy === o.id} onClick={() => verify(o.id, 'reject')} style={{ flex: 1, background: '#fff', color: '#C0392B', border: '1.5px solid #C0392B', borderRadius: 10, padding: '.75rem', fontWeight: 800, fontSize: '.85rem', cursor: 'pointer', fontFamily: "'DM Sans',sans-serif" }}>✕ Reject</button>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
